import { CSSProperties } from 'react';
import { css } from 'styled-components';

import { FontFamilyTheme } from '../theme';
import type { Styled } from '../types/common.types';

export type BaseTextProps = {
  textAlign: CSSProperties['textAlign'];
  color: CSSProperties['color'];
  fontSize: CSSProperties['fontSize'];
  fontWeight: CSSProperties['fontWeight'];
  fontStyle: CSSProperties['fontStyle'];
  lineHeight: CSSProperties['lineHeight'];
  letterSpacing: CSSProperties['letterSpacing'];
  textDecoration: CSSProperties['textDecoration'];
  textTransform: CSSProperties['textTransform'];
  fontFamily: keyof FontFamilyTheme | undefined;
};

export type TextProps = Partial<BaseTextProps>;

export type StyledTextProps = Styled<TextProps>;

/**
 * Styles that can map text props prefixed with `$` to an adequate CSS property
 *
 * @example
 * const Test = styled.div<StyledTextProps>`
 *   ${textStyles}
 * `
 */
export const textStyles = css<StyledTextProps>(
  ({
    theme,
    $textAlign,
    $color,
    $fontSize,
    $fontWeight,
    $fontStyle,
    $lineHeight,
    $letterSpacing,
    $textDecoration,
    $textTransform,
    $fontFamily,
  }) => ({
    textAlign: $textAlign,
    color: $color,
    fontSize: $fontSize,
    fontWeight: $fontWeight,
    fontStyle: $fontStyle,
    lineHeight: $lineHeight,
    letterSpacing: $letterSpacing,
    textDecoration: $textDecoration,
    textTransform: $textTransform,
    /* stylelint-disable */
    fontFamily: $fontFamily
      ? theme.core.typography.fontFamily[$fontFamily]
      : undefined,
    /* styelint-enable */
  }),
);

export const extractTextProps = <T extends TextProps>(
  props: T,
): BaseTextProps => {
  const {
    textAlign,
    color,
    fontSize,
    fontWeight,
    fontStyle,
    lineHeight,
    letterSpacing,
    textDecoration,
    textTransform,
    fontFamily,
  } = props;

  return {
    textAlign,
    color,
    fontSize,
    fontWeight,
    fontStyle,
    lineHeight,
    letterSpacing,
    textDecoration,
    textTransform,
    fontFamily,
  };
};
